import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";

const slides = [
  "Glass-front sauna with tiered timber seating",
  "Sauna interior with timber bench and stone heater",
  "Glass-front sauna beside a bathroom shower",
  "Warmly lit sauna in a wellness room",
];

export function SaunaSlideshow() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(true);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReducedMotion(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (paused || hovered || focused || reducedMotion) return;
    const timer = window.setInterval(() => setActive(i => (i + 1) % slides.length), 5000);
    return () => window.clearInterval(timer);
  }, [paused, hovered, focused, reducedMotion]);

  const select = (index: number) => {
    setActive((index + slides.length) % slides.length);
    setPaused(true);
  };
  const control = "grid h-10 w-10 place-items-center rounded-full bg-black/55 text-white backdrop-blur-md hover:bg-black/80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white";

  return (
    <div className="absolute inset-0" role="region" aria-roledescription="carousel" aria-label="Sauna photographs"
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      onFocusCapture={() => setFocused(true)}
      onBlurCapture={event => { if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false); }}
      onKeyDown={event => {
        if (event.key === "ArrowLeft") { event.preventDefault(); select(active - 1); }
        if (event.key === "ArrowRight") { event.preventDefault(); select(active + 1); }
      }}>
      {slides.map((alt, index) => (
        <img key={alt} src={`${import.meta.env.BASE_URL}images/home-sauna-slide-${index + 1}.webp`}
          alt={alt} aria-hidden={index !== active} loading="lazy" decoding="async" width={1200} height={900}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 motion-reduce:transition-none ${index === active ? "opacity-100" : "opacity-0"}`} />
      ))}
      <div className="absolute z-20 top-5 left-5 right-5 flex items-center justify-between gap-2">
        <div className="flex gap-2">
          <button type="button" className={control} aria-label="Previous sauna photo" onClick={() => select(active - 1)}><ChevronLeft size={18} /></button>
          <button type="button" className={control} aria-label="Next sauna photo" onClick={() => select(active + 1)}><ChevronRight size={18} /></button>
        </div>
        {!reducedMotion && <button type="button" className={control} aria-label={paused ? "Play sauna slideshow" : "Pause sauna slideshow"} onClick={() => setPaused(value => !value)}>
          {paused ? <Play size={16} /> : <Pause size={16} />}
        </button>}
      </div>
      <div className="absolute z-20 left-6 bottom-24 flex items-center rounded-full bg-black/55 px-2">
        {slides.map((alt, index) => (
          <button type="button" key={alt} className="grid h-10 w-9 place-items-center rounded-full focus-visible:outline focus-visible:outline-white"
            aria-label={`Show sauna photo ${index + 1}: ${alt}`} aria-current={index === active ? "true" : undefined} onClick={() => select(index)}>
            <span className={`h-2 rounded-full ${index === active ? "w-5 bg-white" : "w-2 bg-white/45"}`} />
          </button>
        ))}
      </div>
    </div>
  );
}
